import { useEffect, useState } from 'react';
import izenuAPI from '../../services/izenuAPI';
import GENRES from '../../utils/Genres';

const CDN_URL = process.env.REACT_APP_CDN_URL || 'https://dpagz47n4g1lw.cloudfront.net';

// Edit modal for a platform user, shared by the Utilizadores and Artistas lists.
// It needs the row to carry the editable columns (see admin/user/listUsers.php
// and admin/artist/list.php); a missing one would show as empty here.

function formValues(user) {
    return {
        name: user.name || '',
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        real_name: user.real_name || '',
        artist_name: user.artist_name || '',
        email: user.email || '',
        phone: user.phone || '',
        bio: user.bio || '',
        genre: user.genre || '',
        // <input type="date"> only accepts YYYY-MM-DD; a DATETIME would leave it blank.
        birthdate: user.birthdate ? String(user.birthdate).slice(0, 10) : '',
        acc_type: user.acc_type || 'listener',
        verified: user.verified || 'no',
        active: user.active !== undefined && user.active !== null ? String(user.active) : '1',
        distributor_id: user.dist_id ? String(user.dist_id) : '',
        password: '',
    };
}

// A podcaster is acc_type 'creator', linked to a distributor through distributor_podcaster.
const ACC_TYPE_LABELS = { listener: 'Ouvinte', artist: 'Artista', creator: 'Podcaster' };
// Types that belong to a distributor, with how the hint names them.
export const LINKED_TYPES = { artist: 'artista', creator: 'podcaster' };

export default function EditUserModal({ user, distributors, onClose, onSaved }) {
    const [editForm, setEditForm] = useState(() => formValues(user));
    const [editOriginal] = useState(() => formValues(user));
    const [editError, setEditError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [saving, setSaving] = useState(false);
    const [photoFile, setPhotoFile] = useState(null);

    // Only the fields that changed. Re-sending everything made every save of a
    // verified artist reset verified_at, and sent an empty birthdate to a DATE column.
    const editChanges = Object.fromEntries(
        Object.keys(editForm).filter(k => String(editForm[k] ?? '') !== String(editOriginal[k] ?? '')).map(k => [k, editForm[k]])
    );
    const changeCount = Object.keys(editChanges).length + (photoFile ? 1 : 0);
    const editDirty = changeCount > 0;

    const closeEditModal = () => {
        if (saving) return;
        if (editDirty && !window.confirm('Há alterações por guardar. Fechar mesmo assim?')) return;
        onClose();
    };

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') closeEditModal(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    });

    const handleEditChange = (field, value) => setEditForm(prev => ({ ...prev, [field]: value }));

    const saveEdit = async (e) => {
        e?.preventDefault();
        if (!editDirty) { onClose(); return; }
        const fieldsChanged = Object.keys(editChanges).length > 0;
        const payload = { user_id: user.user_id, ...editChanges };
        // Artists and podcasters belong to a distributor; the backend falls back to 1.
        if (payload.acc_type && LINKED_TYPES[payload.acc_type] && editForm.distributor_id) payload.distributor_id = editForm.distributor_id;
        setSaving(true);
        setEditError('');
        try {
            if (fieldsChanged) {
                const { data: res } = await izenuAPI.post('/admin/user/updateUser.php', payload);
                if (!res.response) {
                    setEditError(res.message || 'Erro ao atualizar');
                    setSaving(false);
                    return;
                }
            }
            if (photoFile) {
                const photoError = await uploadUserPhoto(user.user_id, photoFile);
                if (photoError) {
                    setEditError(fieldsChanged ? `Dados guardados, mas a foto falhou: ${photoError}` : photoError);
                    setSaving(false);
                    return;
                }
            }
            onSaved();
            return;
        } catch (error) {
            console.log(error);
            setEditError('Erro ao atualizar utilizador');
        }
        setSaving(false);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        return d.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    const accTypeBadge = (type) => {
        const styles = {
            artist: { background: '#ecfdf5', color: '#059669' },
            listener: { background: '#f0f9ff', color: '#0284c7' },
            creator: { background: '#f5f3ff', color: '#7c3aed' },
            distributor: { background: '#fef3c7', color: '#d97706' },
        };
        const s = styles[type] || styles.listener;
        return (
            <span style={{ fontSize: 11, fontWeight: 600, padding: '1px 8px', borderRadius: 12, ...s }}>
                {ACC_TYPE_LABELS[type || 'listener'] || type}
            </span>
        );
    };

    const verifiedBadge = (v) => {
        if (v === 'yes') {
            return <span style={{ fontSize: 11, fontWeight: 600, padding: '1px 8px', borderRadius: 12, background: '#ecfdf5', color: '#059669' }}>Verificado</span>;
        }
        return null;
    };

    const photo = user.photo ? `${CDN_URL}/users/${user.user_id}/profile_photos/${user.photo}` : null;
    const isArtist = editForm.acc_type === 'artist';
    const hasDistributor = Boolean(LINKED_TYPES[editForm.acc_type]);
    const leavingType = LINKED_TYPES[editOriginal.acc_type] && editOriginal.acc_type !== editForm.acc_type
        ? LINKED_TYPES[editOriginal.acc_type] : null;
    // user.genre holds free text (even countries) on old accounts; keep it selectable.
    const genreNames = GENRES.map(g => g.name);
    const genreOptions = editForm.genre && !genreNames.includes(editForm.genre) ? [editForm.genre, ...genreNames] : genreNames;
    return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 sm:p-4" onClick={closeEditModal}>
        <form onSubmit={saveEdit} onClick={e => e.stopPropagation()}
            className="relative flex w-full sm:max-w-2xl max-h-[94vh] sm:max-h-[90vh] flex-col overflow-hidden rounded-t-2xl sm:rounded-2xl bg-white text-left shadow-xl">

            {/* Header */}
            <div className="flex items-center gap-3 border-b px-4 sm:px-6 py-4">
                <div className="h-12 w-12 shrink-0 overflow-hidden rounded-full bg-gray-200">
                    {photo
                        ? <img src={photo} alt="" className="h-full w-full object-cover" />
                        : <div className="flex h-full w-full items-center justify-center text-lg font-semibold text-gray-500">{(user.artist_name || user.name || '?')[0].toUpperCase()}</div>}
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                        <h3 className="truncate text-lg font-semibold text-gray-900">{user.artist_name || user.name}</h3>
                        {accTypeBadge(user.acc_type)}
                        {verifiedBadge(user.verified)}
                    </div>
                    <p className="truncate text-sm text-gray-500">ID {user.user_id} · {user.email}{user.created_at ? ` · desde ${formatDate(user.created_at)}` : ''}</p>
                </div>
                <button type="button" onClick={closeEditModal} aria-label="Fechar" className="rounded-lg p-1.5 text-2xl leading-none text-gray-400 hover:bg-gray-100 hover:text-gray-700">×</button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5 space-y-6">
                <EditSection title="Identidade">
                    <PhotoField current={photo} file={photoFile} onChange={setPhotoFile} />
                    <Field label="Nome de utilizador" hint="O @ público da conta.">
                        <input className={inputCls} value={editForm.name} onChange={e => handleEditChange('name', e.target.value)} />
                    </Field>
                    <Field label="Nome artístico" hint={isArtist ? 'É o nome mostrado no perfil e nas faixas.' : 'Só é usado em contas de artista.'}>
                        <input className={inputCls} value={editForm.artist_name} onChange={e => handleEditChange('artist_name', e.target.value)} />
                    </Field>
                    <Field label="Primeiro nome">
                        <input className={inputCls} value={editForm.first_name} onChange={e => handleEditChange('first_name', e.target.value)} />
                    </Field>
                    <Field label="Último nome">
                        <input className={inputCls} value={editForm.last_name} onChange={e => handleEditChange('last_name', e.target.value)} />
                    </Field>
                    <Field label="Nome real" wide>
                        <input className={inputCls} value={editForm.real_name} onChange={e => handleEditChange('real_name', e.target.value)} />
                    </Field>
                </EditSection>

                <EditSection title="Contacto">
                    <Field label="Email">
                        <input className={inputCls} type="email" value={editForm.email} onChange={e => handleEditChange('email', e.target.value)} />
                    </Field>
                    <Field label="Telefone">
                        <input className={inputCls} type="tel" value={editForm.phone} onChange={e => handleEditChange('phone', e.target.value)} placeholder="+244 9XX XXX XXX" />
                    </Field>
                </EditSection>

                <EditSection title="Perfil">
                    <Field label="Data de nascimento">
                        <input className={inputCls} type="date" value={editForm.birthdate} onChange={e => handleEditChange('birthdate', e.target.value)} />
                    </Field>
                    <Field label="Género musical">
                        <select className={inputCls} value={editForm.genre} onChange={e => handleEditChange('genre', e.target.value)}>
                            <option value="">Sem género</option>
                            {genreOptions.map(g => <option key={g} value={g}>{g}</option>)}
                        </select>
                    </Field>
                    <Field label="Bio" wide>
                        <textarea className={`${inputCls} min-h-[80px] resize-y`} value={editForm.bio} onChange={e => handleEditChange('bio', e.target.value)} />
                    </Field>
                </EditSection>

                <EditSection title="Conta">
                    <Field label="Tipo de conta" hint={leavingType ? `Deixa de ser ${leavingType} e sai da distribuidora.` : undefined}>
                        <select className={inputCls} value={editForm.acc_type} onChange={e => handleEditChange('acc_type', e.target.value)}>
                            <option value="listener">Ouvinte</option>
                            <option value="artist">Artista</option>
                            <option value="creator">Podcaster</option>
                        </select>
                    </Field>
                    {hasDistributor && (
                        <Field label="Distribuidora">
                            <select className={inputCls} value={editForm.distributor_id} onChange={e => handleEditChange('distributor_id', e.target.value)}>
                                <option value="">Sem distribuidora</option>
                                {distributors.map(d => <option key={d.distributor_id} value={String(d.distributor_id)}>{d.name}</option>)}
                            </select>
                        </Field>
                    )}
                    <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Toggle
                            checked={editForm.verified === 'yes'}
                            onChange={v => handleEditChange('verified', v ? 'yes' : 'no')}
                            label="Verificado"
                            hint="Mostra o selo de verificado no perfil."
                        />
                        <Toggle
                            checked={editForm.active === '1'}
                            onChange={v => handleEditChange('active', v ? '1' : '0')}
                            label="Conta activa"
                            hint="Desligado, a conta fica desactivada."
                        />
                    </div>
                </EditSection>

                <EditSection title="Segurança">
                    <Field label="Nova palavra-passe" hint="Deixa vazio para manter a actual." wide>
                        <div className="relative">
                            <input className={`${inputCls} pe-20`} type={showPassword ? 'text' : 'password'} autoComplete="new-password"
                                value={editForm.password} onChange={e => handleEditChange('password', e.target.value)} />
                            <button type="button" onClick={() => setShowPassword(v => !v)}
                                className="absolute inset-y-0 end-0 px-3 text-xs font-medium text-gray-500 hover:text-gray-800">
                                {showPassword ? 'Esconder' : 'Mostrar'}
                            </button>
                        </div>
                    </Field>
                </EditSection>
            </div>

            {/* Footer */}
            <div className="flex flex-col-reverse gap-2 border-t bg-gray-50 px-4 sm:px-6 py-3 sm:flex-row sm:items-center">
                <span className="text-xs text-gray-500 sm:me-auto">
                    {editError
                        ? <span className="text-rose-600">{editError}</span>
                        : editDirty ? `${changeCount} alteração(ões) por guardar` : 'Sem alterações'}
                </span>
                <button type="button" onClick={closeEditModal} disabled={saving} style={btnSecondary}>Cancelar</button>
                <button type="submit" disabled={saving || !editDirty} style={{ ...btnPrimary, opacity: saving || !editDirty ? 0.5 : 1 }}>
                    {saving ? 'A guardar…' : 'Guardar'}
                </button>
            </div>
        </form>
    </div>
    );

}

// Uploads a profile photo for an existing user. Returns an error message, or null on success.
export async function uploadUserPhoto(userId, file) {
    const form = new FormData();
    form.append('user_id', String(userId));
    form.append('file', file);
    try {
        const { data: res } = await izenuAPI.post('/admin/user/updatePhoto.php', form);
        return res.response ? null : (res.message || 'Erro ao enviar a foto');
    } catch (error) {
        console.log(error);
        return 'Erro ao enviar a foto';
    }
}

const PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

// Profile photo picker: shows the chosen file, else the current photo, else a placeholder.
export function PhotoField({ current, file, onChange }) {
    const [preview, setPreview] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!file) { setPreview(null); return undefined; }
        const url = URL.createObjectURL(file);
        setPreview(url);
        return () => URL.revokeObjectURL(url);
    }, [file]);

    const pick = (e) => {
        const f = e.target.files?.[0];
        e.target.value = '';
        if (!f) return;
        if (!PHOTO_TYPES.includes(f.type)) { setError('A foto tem de ser JPG, PNG ou WebP.'); return; }
        setError('');
        onChange(f);
    };

    const shown = preview || current;
    return (
        <div className="sm:col-span-2 flex items-center gap-4">
            <div className="h-20 w-20 shrink-0 overflow-hidden rounded-full bg-gray-200">
                {shown
                    ? <img src={shown} alt="" className="h-full w-full object-cover" />
                    : <div className="flex h-full w-full items-center justify-center text-xs text-gray-500">Sem foto</div>}
            </div>
            <div>
                <span className="mb-1 block text-sm font-medium text-gray-700">Foto de perfil</span>
                <div className="flex flex-wrap items-center gap-2">
                    <label className="cursor-pointer rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50">
                        {shown ? 'Alterar foto' : 'Escolher foto'}
                        <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={pick} />
                    </label>
                    {file && (
                        <button type="button" onClick={() => onChange(null)} className="text-sm text-gray-500 hover:text-gray-800">
                            Desfazer
                        </button>
                    )}
                </div>
                <span className={`mt-1 block text-xs ${error ? 'text-rose-600' : 'text-gray-500'}`}>
                    {error || 'JPG, PNG ou WebP. Quadrada fica melhor.'}
                </span>
            </div>
        </div>
    );
}

export const inputCls = 'block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:ring-indigo-500';

export function EditSection({ title, children }) {
    return (
        <section>
            <h4 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">{title}</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>
        </section>
    );
}

export function Field({ label, hint, wide, children }) {
    return (
        <label className={`block ${wide ? 'sm:col-span-2' : ''}`}>
            <span className="mb-1 block text-sm font-medium text-gray-700">{label}</span>
            {children}
            {hint && <span className="mt-1 block text-xs text-gray-500">{hint}</span>}
        </label>
    );
}

export function Toggle({ checked, onChange, label, hint }) {
    return (
        <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 p-3 hover:bg-gray-50">
            <input type="checkbox" className="sr-only peer" checked={checked} onChange={e => onChange(e.target.checked)} />
            <span aria-hidden="true" className={`relative mt-0.5 h-5 w-9 shrink-0 rounded-full transition ${checked ? 'bg-indigo-500' : 'bg-gray-300'} peer-focus-visible:ring-2 peer-focus-visible:ring-indigo-400`}>
                <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${checked ? 'left-[18px]' : 'left-0.5'}`} />
            </span>
            <span>
                <span className="block text-sm font-medium text-gray-800">{label}</span>
                {hint && <span className="block text-xs text-gray-500">{hint}</span>}
            </span>
        </label>
    );
}


export const btnPrimary = { padding: '8px 20px', fontSize: 14, fontWeight: 600, borderRadius: 8, border: 'none', background: '#6366f1', color: '#fff', cursor: 'pointer' };
export const btnSecondary = { padding: '8px 20px', fontSize: 14, fontWeight: 600, borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' };
